/**
 * Frontend API client for Revisr knowledge-tag endpoints.
 * Calls /api/revisr/knowledge-tags/* — the bridge between
 * the knowledge plugin and revisr.revisr_library_tags.
 */

import { supabase } from '@foundation/utils/supabaseClient';
import type { RevisrTag, TagAssignPayload, DocTagsMap, CreateTagParams } from '../types/knowledgeOverrides';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001';

const getAuthHeaders = async (): Promise<Record<string, string>> => {
  const { data: { session } } = await supabase.auth.getSession();
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${session?.access_token ?? ''}`,
    'x-user-id': session?.user?.id ?? '',
  };
};

// ─── Tags ─────────────────────────────────────────────────────────────────────

export const getTags = async (): Promise<RevisrTag[]> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/knowledge-tags/tags`, { headers });
  if (!res.ok) throw new Error(`Failed to fetch tags: ${res.status}`);
  return res.json() as Promise<RevisrTag[]>;
};

export const searchTags = async (q: string, limit = 50): Promise<RevisrTag[]> => {
  const headers = await getAuthHeaders();
  const res = await fetch(
    `${API_BASE_URL}/api/revisr/knowledge-tags/tags/search?q=${encodeURIComponent(q)}&limit=${limit}`,
    { headers }
  );
  if (!res.ok) throw new Error(`Failed to search tags: ${res.status}`);
  return res.json() as Promise<RevisrTag[]>;
};

export const createTag = async (params: CreateTagParams): Promise<RevisrTag> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/knowledge-tags/tags`, {
    method: 'POST',
    headers,
    body: JSON.stringify(params),
  });
  if (!res.ok) throw new Error(`Failed to create tag: ${res.status}`);
  return res.json() as Promise<RevisrTag>;
};

// ─── Tag Assignments ──────────────────────────────────────────────────────────

export const assignTags = async (payload: TagAssignPayload): Promise<void> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/knowledge-tags/assign`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to assign tags: ${res.status}`);
};

export const removeTags = async (payload: TagAssignPayload): Promise<void> => {
  const headers = await getAuthHeaders();
  const res = await fetch(`${API_BASE_URL}/api/revisr/knowledge-tags/assign`, {
    method: 'DELETE',
    headers,
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`Failed to remove tags: ${res.status}`);
};

export const getTagsByDocs = async (docIds: string[]): Promise<DocTagsMap> => {
  if (docIds.length === 0) return {};
  const headers = await getAuthHeaders();
  const res = await fetch(
    `${API_BASE_URL}/api/revisr/knowledge-tags/by-doc?doc_ids=${docIds.map(encodeURIComponent).join(',')}`,
    { headers }
  );
  if (!res.ok) throw new Error(`Failed to fetch tags by doc: ${res.status}`);
  return res.json() as Promise<DocTagsMap>;
};

// ─── Documents with original file paths ───────────────────────────────────────

export type DocWithFilepath = {
  id: string;
  name: string;
  filepath: string;
  category: string | null;
};

/**
 * Fetches documents that have an original file stored in Supabase Storage.
 * Uses GET /api/revisr/knowledge/documents (knowledge-base-overrides endpoint).
 */
export const getDocumentsWithFiles = async (orgId: string): Promise<DocWithFilepath[]> => {
  const baseHeaders = await getAuthHeaders();
  const headers = { ...baseHeaders, 'x-org-id': orgId };
  const res = await fetch(`${API_BASE_URL}/api/revisr/knowledge/documents`, { headers });
  if (!res.ok) return [];
  const json = await res.json() as { documents: Array<{ id: string; name: string; filepath: string | null; category: string | null }> };
  return (json.documents ?? []).filter((d) => !!d.filepath) as DocWithFilepath[];
};
